import React, { useCallback, useEffect, useState } from 'react';
import { platformCut } from './feeMath';
import VehicleRates from './VehicleRates';
import './FeeSettings.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY = process.env.REACT_APP_BACKEND_API_KEY || '';

async function api(path, opts = {}) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const tzs = (n) => `TZS ${Math.round(Number(n) || 0).toLocaleString('en-TZ')}`;

/** An order worth working the sums on: typical for a bag of fertiliser. */
const EXAMPLE_ORDER = 50000;

/**
 * What Beetle charges, in one place.
 *
 * The cut per order used to sit on Finance and the delivery rates on Delivery
 * Settings, and neither said what the other would do to an order. Changing
 * the cut from a flat 100 TZS to a percentage is the sort of decision that
 * wants to be read, checked against a real order, and saved — so the example
 * below is worked out from whatever is typed.
 *
 * Delivery itself is priced per vehicle, below.
 */
const FeeSettings = ({ onToast }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingCut, setSavingCut] = useState(false);

  // Our cut per order, from the backend.
  const [sellerType, setSellerType] = useState('flat');
  const [sellerValue, setSellerValue] = useState('');
  const [driverType, setDriverType] = useState('flat');
  const [driverValue, setDriverValue] = useState('');
  const [minPayout, setMinPayout] = useState('');

  const toast = useCallback((message, type = 'success') => {
    if (onToast) onToast(message, type);
  }, [onToast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const config = await api('/api/admin/config/payment');
      setSellerType(config.seller_commission_type || 'flat');
      setSellerValue(String(config.seller_commission_value ?? ''));
      setDriverType(config.driver_commission_type || 'flat');
      setDriverValue(String(config.driver_commission_value ?? ''));
      setMinPayout(String(config.min_payout_amount ?? ''));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCut = async (e) => {
    e.preventDefault();
    setSavingCut(true);
    try {
      await api('/api/admin/config/payment', {
        method: 'PUT',
        body: JSON.stringify({
          seller_commission_type: sellerType,
          seller_commission_value: Number(sellerValue),
          driver_commission_type: driverType,
          driver_commission_value: Number(driverValue),
          min_payout_amount: Number(minPayout),
        }),
      });
      toast('Saved. New orders use this from now on.');
      load();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSavingCut(false);
    }
  };

  // What the numbers on screen would do to one real order.
  const cutOnExample = platformCut(sellerType, sellerValue, EXAMPLE_ORDER);

  if (loading) {
    return <div className="fee-settings-loading">Loading fees…</div>;
  }

  return (
    <div className="fee-settings">
      {error && <div className="fee-settings-error">{error}</div>}

      {/* ── What Beetle takes from an order ── */}
      <form className="fee-card" onSubmit={saveCut}>
        <div className="fee-card-head">
          <h3>What Beetle charges per order</h3>
          <p>
            Taken off the goods total, not the delivery fee. ClickPesa's own
            collection fee comes off the seller's side.
          </p>
        </div>

        <div className="fee-row">
          <label>
            <span>How it is charged</span>
            <select value={sellerType} onChange={(e) => setSellerType(e.target.value)}>
              <option value="flat">A fixed amount per order</option>
              <option value="percentage">A percentage of the order</option>
            </select>
          </label>

          <label>
            <span>{sellerType === 'percentage' ? 'Percentage (%)' : 'Amount (TZS)'}</span>
            <input
              type="number"
              min="0"
              step={sellerType === 'percentage' ? '0.1' : '1'}
              value={sellerValue}
              onChange={(e) => setSellerValue(e.target.value)}
              required
            />
          </label>
        </div>

        <p className="fee-example">
          On a {tzs(EXAMPLE_ORDER)} order Beetle takes <strong>{tzs(cutOnExample)}</strong>
          {sellerType === 'percentage' && Number(sellerValue) > 0 && (
            <> — {sellerValue}% of the goods</>
          )}
        </p>

        <div className="fee-row">
          <label>
            <span>Taken from the driver's fee</span>
            <select value={driverType} onChange={(e) => setDriverType(e.target.value)}>
              <option value="flat">A fixed amount</option>
              <option value="percentage">A percentage</option>
            </select>
          </label>

          <label>
            <span>{driverType === 'percentage' ? 'Percentage (%)' : 'Amount (TZS)'}</span>
            <input
              type="number"
              min="0"
              step={driverType === 'percentage' ? '0.1' : '1'}
              value={driverValue}
              onChange={(e) => setDriverValue(e.target.value)}
            />
          </label>
        </div>

        <div className="fee-row">
          <label>
            <span>Smallest payout worth sending (TZS)</span>
            <input
              type="number"
              min="0"
              value={minPayout}
              onChange={(e) => setMinPayout(e.target.value)}
            />
          </label>
          <div className="fee-note">
            Below this, a seller's money waits rather than being sent — each
            payout costs a provider fee of its own.
          </div>
        </div>

        <button type="submit" className="fee-save" disabled={savingCut}>
          {savingCut ? 'Saving…' : 'Save order charges'}
        </button>
      </form>

      {/* One rate table for delivery: the vehicles themselves. */}
      <VehicleRates onToast={onToast} />
    </div>
  );
};

export default FeeSettings;
